<template>
  <div class="svg-hosting-page">
    <!-- 顶部标题区：说明这篇文章要解决什么问题 -->
    <header class="page-hero">
      <div class="hero-main">
        <h1>用 GitHub Pages 托管 SVG 动画图床</h1>
        <p>
          把 AI 生成的 SVG 动画丢进 GitHub 仓库，直接当图床用，论坛里用 Markdown 一行就能展示，
          动画还会照常播放。这篇就是完整的操作流程。
        </p>
        <div class="hero-tags">
          <span class="hero-tag">GitHub Pages</span>
          <span class="hero-tag">SVG 动画</span>
          <span class="hero-tag">Markdown 外链</span>
          <span class="hero-tag">免图床</span>
        </div>
      </div>
      <div class="hero-aside">
        <div class="aside-title">目标产物</div>
        <div class="aside-url">https://kohaiy.github.io/assets/images/pelican-bicycle-night.svg</div>
        <div class="aside-note">仓库路径直接映射成 URL，没有额外配置</div>
      </div>
    </header>

    <div class="page-body">
      <!-- 左侧主内容：教程正文 -->
      <main class="doc-main">
        <section
          v-for="chapter in chapters"
          :key="chapter.id"
          :id="chapter.id"
          class="doc-section"
        >
          <div class="section-head">
            <span class="section-index">{{ chapter.index }}</span>
            <div class="section-titles">
              <h2>{{ chapter.title }}</h2>
              <p v-if="chapter.desc">{{ chapter.desc }}</p>
            </div>
          </div>

          <div class="section-content">
            <!-- 步骤列表：带编号的操作步骤 -->
            <ol v-if="chapter.steps" class="step-list">
              <li v-for="(step, stepIndex) in chapter.steps" :key="stepIndex">
                <div class="step-title">{{ step.title }}</div>
                <div v-if="step.detail" class="step-detail">{{ step.detail }}</div>

                <!-- 路径面包屑：Settings → Pages -->
                <div v-if="step.path" class="path-chain">
                  <template v-for="(node, nodeIndex) in step.path">
                    <span :key="`node-${nodeIndex}`" class="path-node">{{ node }}</span>
                    <i
                      v-if="nodeIndex < step.path.length - 1"
                      :key="`arrow-${nodeIndex}`"
                      class="el-icon-right path-arrow"
                    />
                  </template>
                </div>

                <!-- 代码块：shell / 文本类内容 -->
                <pre
                  v-if="step.code"
                  class="code-block"
                ><code>{{ step.code }}</code></pre>
              </li>
            </ol>

            <!-- 普通段落 -->
            <p
              v-for="(paragraph, pIndex) in chapter.paragraphs || []"
              :key="`p-${pIndex}`"
              class="doc-paragraph"
            >{{ paragraph }}</p>

            <!-- 要点卡片 -->
            <ul v-if="chapter.points" class="point-list">
              <li v-for="(point, pointIndex) in chapter.points" :key="pointIndex">
                <i class="el-icon-check point-icon" />
                <span v-html="point" />
              </li>
            </ul>

            <!-- 目录树 -->
            <pre v-if="chapter.tree" class="tree-block"><code>{{ chapter.tree }}</code></pre>

            <!-- Markdown 图片语法示例（带一键复制） -->
            <div v-if="chapter.markdown" class="markdown-card">
              <div class="markdown-head">
                <span class="markdown-label">Markdown 写法</span>
                <el-button
                  type="text"
                  size="mini"
                  class="copy-button"
                  @click="copyText(chapter.markdown, 'markdown')"
                >
                  {{ copiedKey === 'markdown' ? '已复制' : '复制代码' }}
                </el-button>
              </div>
              <pre class="code-block code-block--inline"><code>{{ chapter.markdown }}</code></pre>
            </div>

            <!-- 最终访问地址（带一键复制） -->
            <div v-if="chapter.finalUrl" class="url-card">
              <div class="url-head">
                <span class="url-label">最终访问地址</span>
                <el-button
                  type="text"
                  size="mini"
                  class="copy-button"
                  @click="copyText(chapter.finalUrl, 'url')"
                >
                  {{ copiedKey === 'url' ? '已复制' : '复制链接' }}
                </el-button>
              </div>
              <a class="url-text" :href="chapter.finalUrl" target="_blank" rel="noopener">
                {{ chapter.finalUrl }}
              </a>
            </div>

            <!-- 提示条 -->
            <div v-if="chapter.tip" class="tip-bar">
              <i class="el-icon-info tip-icon" />
              <span>{{ chapter.tip }}</span>
            </div>
          </div>
        </section>

        <!-- 收尾：整体流程总览 -->
        <section class="doc-section doc-section--summary">
          <div class="section-head">
            <span class="section-index">✓</span>
            <div class="section-titles">
              <h2>完事</h2>
              <p>整条链路只有两步：文件进仓库，URL 直接引用。</p>
            </div>
          </div>
          <div class="section-content">
            <div class="flow-row">
              <div v-for="(flow, flowIndex) in flowSteps" :key="flowIndex" class="flow-item">
                <div class="flow-index">{{ flowIndex + 1 }}</div>
                <div class="flow-text">{{ flow }}</div>
              </div>
            </div>
            <p class="doc-paragraph">
              以后遇到需要在论坛里直接展示的 SVG 动画，不用再找支持 SVG 的专门图床了，直接丢这里。
              文件不大、源码是纯文本，放 GitHub 仓库里正好。
            </p>
          </div>
        </section>
      </main>

      <!-- 右侧目录：锚点导航 -->
      <aside class="doc-aside">
        <div class="toc-card">
          <div class="toc-title">目录</div>
          <ul class="toc-list">
            <li
              v-for="chapter in chapters"
              :key="`toc-${chapter.id}`"
              class="toc-item"
              :class="{ 'is-active': activeId === chapter.id }"
              @click="scrollTo(chapter.id)"
            >
              <span class="toc-index">{{ chapter.index }}</span>
              <span class="toc-text">{{ chapter.title }}</span>
            </li>
          </ul>
        </div>

        <div class="toc-card">
          <div class="toc-title">核心结论</div>
          <div class="fact-row">
            <span>仓库名</span>
            <strong>kohaiy.github.io</strong>
          </div>
          <div class="fact-row">
            <span>分支</span>
            <strong>main / (root)</strong>
          </div>
          <div class="fact-row">
            <span>存放目录</span>
            <strong>assets/images/</strong>
          </div>
          <div class="fact-row">
            <span>URL 规则</span>
            <strong>仓库路径直映</strong>
          </div>
          <div class="fact-row">
            <span>动画</span>
            <strong>外链照样播放</strong>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
// 教程章节数据：整篇文章的正文全部由这份数据驱动渲染。
const CHAPTERS = [
  {
    id: 'create-repo',
    index: '01',
    title: '建一个 GitHub 仓库',
    desc: '用户名 kohaiy 的话，仓库名必须是 kohaiy.github.io，这是 Pages 的约定。',
    paragraphs: [
      '如果 GitHub 用户名是 kohaiy，仓库就叫 kohaiy.github.io。这个命名不是随便起的——GitHub Pages 对「用户名.github.io」这种仓库有特殊待遇，建好就能直接当站点根域名用。'
    ],
    points: [
      '仓库名：<code>kohaiy.github.io</code>',
      '可见性：Public（私有仓库的 Pages 在免费账号下不可用）',
      '仓库名必须与用户名完全一致，否则访问地址会变成子路径'
    ]
  },
  {
    id: 'enable-pages',
    index: '02',
    title: '到 Settings → Pages 开启',
    desc: '选择从分支部署，指定 main 分支的根目录。',
    steps: [
      {
        title: '进入仓库设置',
        detail: '打开仓库页面，点右上角的 Settings。',
        path: ['仓库', 'Settings', 'Pages']
      },
      {
        title: '选择部署来源',
        detail: '在 Build and deployment 里，Source 选择 Deploy from a branch。',
        path: ['Source', 'Deploy from a branch']
      },
      {
        title: '指定分支与目录',
        detail: 'Branch 选 main，目录选 / (root)，然后点 Save。',
        path: ['Branch', 'main', '/ (root)']
      },
      {
        title: '等它部署完成',
        detail: '保存后 GitHub 会跑一次部署，第一次通常需要一两分钟。刷新 Pages 页面，看到绿色提示与访问地址就说明好了。'
      }
    ],
    tip: '分支目录选 / (root) 之后，仓库里的文件路径会原样变成 URL 路径，后面放 SVG 就是靠这一点。'
  },
  {
    id: 'assets-folder',
    index: '03',
    title: '建目录 assets/images/',
    desc: '在仓库里建好目录，把 SVG 文件扔进去。',
    paragraphs: [
      '在仓库里建一个目录：assets/images/，把 SVG 扔进去就行。目录名没有强制要求，用 assets/images 只是习惯，方便以后同类资源归堆。'
    ],
    tree: `assets/
└── images/
    └── 20260913094941.svg`,
    tip: '文件名建议直接用生成时间戳（比如 20260913094941.svg），天然不重名，也好回溯是哪次生成的。'
  },
  {
    id: 'url-mapping',
    index: '04',
    title: 'Push 之后就有访问地址了',
    desc: '仓库路径直接映射成 URL，没有中间层。',
    paragraphs: [
      'Push 上去以后，对应的访问地址就是：'
    ],
    code: 'https://kohaiy.github.io/assets/images/20260913094941.svg',
    points: [
      '规则：<code>https://用户名.github.io/</code> + 仓库内的文件相对路径',
      '没开 Pages 的自定义域名前，这个地址就是最终地址',
      '可以直接浏览器打开验证，也可以在论坛里被当作图片加载'
    ]
  },
  {
    id: 'post-forum',
    index: '05',
    title: '发论坛',
    desc: '论坛支持 Markdown 图片的话，一行就够了。',
    paragraphs: ['如果论坛支持 Markdown 图片：'],
    markdown: '![鹈鹕骑自行车](https://kohaiy.github.io/assets/images/20260913094941.svg)',
    points: [
      'SVG 直接作为图片加载，<strong>里面的动画也会跟着跑</strong>',
      '对普通图片没什么区别，但 AI 生成的 SVG 动画就很有用',
      '不需要论坛本身支持上传 SVG，也不需要专门的 SVG 图床'
    ],
    tip: '如果论坛会抓取外链图片做代理或压缩，优先选原图直链；部分论坛会过滤 SVG，遇到不显示可以先在浏览器直开链接确认文件本身没问题。'
  },
  {
    id: 'pelican-battle',
    index: '06',
    title: '幽默鹈鹕大赛',
    desc: '为什么这件事值得顺手做一下。',
    paragraphs: [
      '虽然东西很抽象，但 SVG 本身还挺适合这种实验的。文件不大，源码也是文本，放 GitHub 仓库里正好。所以才顺手搞了这个 Pages。'
    ],
    points: [
      '体积小：纯文本文件，比 GIF / 视频小一个量级',
      '可版本管理：每次改动都有 diff，能回滚',
      '可直接改：SVG 是文本，改颜色改动画曲线改个数字就行'
    ]
  },
  {
    id: 'final',
    index: '07',
    title: '目前的目录结构',
    desc: '最终形态就是这样。',
    tree: `GitHub
└── kohaiy.github.io
    └── assets
        └── images
            └── pelican-bicycle-night.svg`,
    finalUrl: 'https://kohaiy.github.io/assets/images/pelican-bicycle-night.svg'
  }
]

export default {
  name: 'PcOnlineTeachingSvgImageHosting',

  data() {
    return {
      // 章节正文数据
      chapters: CHAPTERS,
      // 当前高亮的目录项
      activeId: CHAPTERS[0].id,
      // 最近一次复制的目标标识，用于按钮文案反馈
      copiedKey: '',
      // 复制反馈的复位定时器
      copyTimer: null
    }
  },

  computed: {
    // 收尾部分展示的流程总览
    flowSteps() {
      return [
        '建仓库 kohaiy.github.io',
        'Settings → Pages 选 main / (root)',
        '提交 SVG 到 assets/images/',
        '论坛里用 Markdown 直链引用'
      ]
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll, true)
    this.handleScroll()
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll, true)
    if (this.copyTimer) {
      clearTimeout(this.copyTimer)
      this.copyTimer = null
    }
  },

  methods: {
    // 点击目录项时平滑滚动到对应章节
    scrollTo(id) {
      const target = document.getElementById(id)
      if (!target) return
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.activeId = id
    },

    // 滚动时根据章节位置更新目录高亮
    handleScroll() {
      const offset = 140
      let current = this.activeId

      this.chapters.forEach(chapter => {
        const el = document.getElementById(chapter.id)
        if (!el) return
        if (el.getBoundingClientRect().top <= offset) {
          current = chapter.id
        }
      })

      if (current !== this.activeId) {
        this.activeId = current
      }
    },

    // 复制文本到剪贴板，并给出短暂的按钮反馈
    copyText(text, key) {
      const done = () => {
        this.copiedKey = key
        if (this.copyTimer) clearTimeout(this.copyTimer)
        this.copyTimer = setTimeout(() => {
          this.copiedKey = ''
        }, 1800)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done))
        return
      }

      this.fallbackCopy(text, done)
    },

    // 兼容不支持 clipboard API 的环境（如 http 下的部分浏览器）
    fallbackCopy(text, done) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        document.execCommand('copy')
        done()
      } catch (error) {
        this.$toast.warning('当前浏览器不支持自动复制，请手动选中复制')
      }

      document.body.removeChild(textarea)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/breakpoints.scss';

.svg-hosting-page {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
  background: transparent;
}

// ===== 顶部标题区 =====
.page-hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
  padding: 24px 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1f2d3d 0%, #31506f 55%, #3f6d9e 100%);
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.18);
}

.hero-main {
  min-width: 0;
  max-width: 720px;
}

.hero-main h1 {
  margin: 0 0 12px;
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.5px;

  @include bp-middle {
    font-size: 23px;
  }

  @include bp-small {
    font-size: 22px;
  }
}

.hero-main p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
  line-height: 1.75;

  @include bp-middle {
    font-size: 13px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.hero-tag {
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;

  @include bp-small {
    font-size: 12px;
  }
}

.hero-aside {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 360px;
  padding: 16px 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);

  @include bp-middle {
    width: 340px;
  }

  @include bp-small {
    width: 320px;
  }
}

.aside-title {
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.aside-url {
  color: #fff;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-all;

  @include bp-small {
    font-size: 12px;
  }
}

.aside-note {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  line-height: 1.6;
}

// ===== 主体双栏 =====
.page-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 18px;
  align-items: start;

  @include bp-middle {
    grid-template-columns: minmax(0, 1fr) 280px;
  }

  @include bp-small {
    grid-template-columns: minmax(0, 1fr) 260px;
  }
}

.doc-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ===== 章节卡片 =====
.doc-section {
  padding: 22px 24px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: var(--surface-1);
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.06);
  scroll-margin-top: 90px;
}

.doc-section--summary {
  border-color: #d8e7d8;
  background: var(--surface-1);
}

.section-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #e8eef6;
}

.section-index {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #ecf5ff;
  color: #315dcf;
  font-size: 14px;
  font-weight: 600;

  @include bp-middle {
    width: 32px;
    height: 32px;
    font-size: 13px;
  }

  @include bp-small {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }
}

.section-titles {
  min-width: 0;
}

.section-titles h2 {
  margin: 0 0 8px;
  color: #1f2d3d;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;

  @include bp-middle {
    font-size: 17px;
  }

  @include bp-small {
    font-size: 16px;
  }
}

.section-titles p {
  margin: 0;
  color: #7a8797;
  font-size: 13px;
  line-height: 1.7;

  @include bp-middle {
    font-size: 12px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.section-content {
  padding-top: 16px;
}

.doc-paragraph {
  margin: 0 0 12px;
  color: #42536a;
  font-size: 14px;
  line-height: 1.85;

  &:last-child {
    margin-bottom: 0;
  }

  @include bp-middle {
    font-size: 13px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 步骤列表 =====
.step-list {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;
}

.step-list > li {
  position: relative;
  padding: 0 0 18px 34px;
  counter-increment: step;

  // 步骤编号圆点
  &::before {
    content: counter(step);
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #315dcf;
    color: #fff;
    font-size: 12px;
    line-height: 1;
  }

  // 左侧连接竖线
  &::after {
    content: '';
    position: absolute;
    top: 26px;
    left: 10px;
    bottom: 4px;
    width: 1px;
    background: #dfe9f5;
  }

  &:last-child {
    padding-bottom: 0;

    &::after {
      display: none;
    }
  }
}

.step-title {
  color: #1f2d3d;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.6;

  @include bp-middle {
    font-size: 13px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.step-detail {
  margin-top: 6px;
  color: #667085;
  font-size: 13px;
  line-height: 1.75;

  @include bp-middle {
    font-size: 12px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 路径链路 =====
.path-chain {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.path-node {
  padding: 4px 10px;
  border: 1px solid #d6e4ff;
  border-radius: 4px;
  background: #f2f7ff;
  color: #315dcf;
  font-size: 12px;
  white-space: nowrap;
}

.path-arrow {
  color: #a8b6c8;
  font-size: 12px;
}

// ===== 代码块 =====
.code-block {
  margin: 12px 0 0;
  padding: 12px 14px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
  color: #2c3e50;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;

  @include bp-small {
    font-size: 12px;
  }
}

.code-block--inline {
  margin-top: 0;
}

// ===== 目录树 =====
.tree-block {
  margin: 12px 0 0;
  padding: 14px 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
  color: #42536a;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.9;
  overflow-x: auto;

  @include bp-small {
    font-size: 12px;
  }
}

// ===== Markdown 示例卡片 =====
.markdown-card,
.url-card {
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
}

.markdown-head,
.url-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.markdown-label,
.url-label {
  color: #7a8797;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.copy-button {
  padding: 0;
  color: #315dcf;
  font-size: 12px;
}

.url-text {
  display: block;
  color: #315dcf;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.7;
  text-decoration: none;
  word-break: break-all;
  transition: color 0.15s;

  &:hover {
    color: #1f4bb8;
    text-decoration: underline;
  }

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 要点列表 =====
.point-list {
  margin: 12px 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 0;
    color: #42536a;
    font-size: 14px;
    line-height: 1.75;

    @include bp-middle {
      font-size: 13px;
    }

    @include bp-small {
      font-size: 12px;
    }
  }

  // 要点内嵌的代码片段
  ::v-deep code {
    padding: 2px 6px;
    border-radius: 3px;
    background: #eef3fa;
    color: #315dcf;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
  }

  strong {
    color: #1f2d3d;
  }
}

.point-icon {
  flex: none;
  margin-top: 5px;
  color: #52a552;
  font-size: 13px;
}

// ===== 提示条 =====
.tip-bar {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  border-left: 3px solid #409eff;
  border-radius: 4px;
  background: #f2f8ff;
  color: #4a5a70;
  font-size: 13px;
  line-height: 1.75;

  @include bp-middle {
    font-size: 12px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.tip-icon {
  flex: none;
  margin-top: 3px;
  color: #409eff;
}

// ===== 流程总览 =====
.flow-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @include bp-middle {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include bp-small {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.flow-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
}

.flow-index {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eef3fa;
  color: #315dcf;
  font-size: 12px;
  font-weight: 600;
}

.flow-text {
  color: #42536a;
  font-size: 13px;
  line-height: 1.6;

  @include bp-middle {
    font-size: 12px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 右侧目录 =====
.doc-aside {
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toc-card {
  padding: 16px 18px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: var(--surface-1);
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.06);
}

.toc-title {
  margin-bottom: 12px;
  color: #1f2d3d;
  font-size: 14px;
  font-weight: 600;

  @include bp-small {
    font-size: 13px;
  }
}

.toc-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.toc-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 5px;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: #f4f8ff;
    color: #315dcf;
  }

  &.is-active {
    background: #ecf5ff;
    color: #315dcf;
    font-weight: 600;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.toc-index {
  flex: none;
  color: #a8b6c8;
  font-size: 12px;
  font-weight: 500;
}

.fact-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #eef2f7;

  &:last-child {
    border-bottom: none;
  }

  span {
    color: #909399;
    font-size: 12px;
    white-space: nowrap;
  }

  strong {
    color: #42536a;
    font-size: 12px;
    font-weight: 600;
    text-align: right;
    word-break: break-all;
  }
}
</style>
